
import { Clip, Track, SubtitleStyle } from './types';
import { calculateChunkBoundaries, getClipsForChunk, detectHeavyEffects } from './utils/timeline-utils';
import { getServerExportClient } from './server-export-client';
import { WebCodecsExporter, ExportConfig, DEFAULT_EXPORT_CONFIG, ASPECT_RATIOS } from './webcodecs-exporter';
import { downloadChunkToFFmpegFS, mergeChunksWithFFmpeg } from './client-merge';

interface ExportProgress {
    phase: 'analyzing' | 'processing' | 'merging' | 'complete' | 'failed';
    progress: number;
    message: string;
    chunks?: any[];
}

export class SmartHybridExporter {
    private userId: string;
    private projectId: string;
    private exportConfig: ExportConfig;
    private onProgress: (p: ExportProgress) => void;
    private aborted = false;

    constructor(
        userId: string,
        projectId: string,
        onProgress: (p: ExportProgress) => void,
        exportConfig?: Partial<ExportConfig>
    ) {
        this.userId = userId;
        this.projectId = projectId;
        this.onProgress = onProgress;
        // Merge provided config with defaults so aspect ratio, fps, bitrates are correct
        this.exportConfig = { ...DEFAULT_EXPORT_CONFIG, ...exportConfig };
    }

    abort() {
        this.aborted = true;
    }

    async export(
        clips: Clip[],
        tracks: Track[],
        activeSubtitleLanguage: string,
        subtitleStyle: SubtitleStyle,
        burnSubtitles: boolean
    ): Promise<Blob> {
        try {
            this.onProgress({ phase: 'analyzing', progress: 0, message: 'Analyse de la timeline...' });

            const totalDuration = Math.max(...clips.map(c => c.start + c.duration), 0);

            // 1. Calculate chunks
            // Use 30s chunks to balance parallelism and overhead
            const boundaries = calculateChunkBoundaries(totalDuration, clips, 30);

            // Map internal chunks to ChunkStatus for UI
            const chunks: Array<{
                index: number;
                start: number;
                end: number;
                mode: 'local' | 'server';
                url?: string;
                blob?: Blob;
                // UI Status
                serverStatus: 'pending' | 'processing' | 'encoding' | 'complete' | 'failed';
                serverProgress: number;
                localStatus: 'waiting' | 'downloading' | 'downloaded' | 'failed';
                localProgress: number;
                size: number;
            }> = [];

            for (let i = 0; i < boundaries.length - 1; i++) {
                const start = boundaries[i];
                const end = boundaries[i + 1];

                // Route logic: Heavy effects? -> Local
                const isHeavy = detectHeavyEffects(clips, start, end, tracks);
                chunks.push({
                    index: i,
                    start,
                    end,
                    mode: isHeavy ? 'local' : 'server',
                    serverStatus: isHeavy ? 'pending' : 'pending',
                    serverProgress: 0,
                    localStatus: 'waiting',
                    localProgress: 0,
                    size: 0
                });
            }

            console.log(`[SmartHybrid] Split into ${chunks.length} chunks:`, chunks);

            // Helper to emit progress with chunks
            const emitProgress = (phase: ExportProgress['phase'], progress: number, message: string) => {
                this.onProgress({
                    phase,
                    progress,
                    message,
                    chunks: chunks.map(c => ({
                        index: c.index,
                        mode: c.mode, // Pass mode to UI
                        serverStatus: c.mode === 'local' ? 'pending' : c.serverStatus, // Local chunks don't use server status visually
                        serverProgress: c.serverProgress,
                        url: c.url || null,
                        localStatus: c.localStatus,
                        localProgress: c.localProgress,
                        size: c.size
                    }))
                });
            };

            emitProgress('processing', 5, `Traitement de ${chunks.length} parties...`);

            // 2. Process chunks
            // We process Server chunks in parallel (they are async jobs)
            // We process Local chunks sequentially (to avoid GPU OOM)

            // Helper to update total progress
            let completedChunks = 0;
            const updateAggregatedProgress = () => {
                const pct = 5 + Math.round((completedChunks / chunks.length) * 85);
                emitProgress('processing', pct, `Traitement: ${completedChunks}/${chunks.length} parties`);
            };

            const serverClient = getServerExportClient(this.userId);

            // Track which local chunk is currently processing to update its progress
            let currentLocalChunkIndex = -1;

            const localExporter = new WebCodecsExporter(
                // Use the actual project config (aspect ratio, fps, bitrates)
                // so local chunks are compatible with server chunks in the final concat.
                this.exportConfig,
                (p) => {
                    // Update local chunk progress
                    if (currentLocalChunkIndex !== -1) {
                        const chunk = chunks.find(c => c.index === currentLocalChunkIndex);
                        if (chunk) {
                            chunk.localProgress = Math.round(p.progress);
                            // Force emit progress to update UI 
                            // (throttle this if needed, but for local export 30fps it's okay)
                            updateAggregatedProgress();
                        }
                    }
                }
            );

            // A. Dispatch Server Jobs
            const serverPromises = chunks
                .filter(c => c.mode === 'server')
                .map(async (chunk) => {
                    if (this.aborted) return;

                    chunk.serverStatus = 'processing';
                    updateAggregatedProgress();

                    // Slice clips for this chunk
                    const chunkClips = getClipsForChunk(clips, chunk.start, chunk.end);

                    const chunkJobId = `hybrid_${this.projectId}_${chunk.index}_${Date.now()}`;

                    // Fire export job
                    // We use a custom key to make it easy to debug
                    const outputKey = `exports/${chunkJobId}.mp4`;

                    try {
                        // Track fetch promise to detect silent network failures (e.g. browser connection limits)
                        const { fetchPromise } = await serverClient.submitSingleChunkJob(
                            chunkJobId,
                            {
                                projectId: this.projectId,
                                clips: chunkClips,
                                tracks,
                                activeSubtitleLanguage,
                                subtitleStyle,
                                quality: 'high', // Always high quality for hybrid
                                burnSubtitles,
                                resolution: ASPECT_RATIOS[this.exportConfig.aspectRatio],
                            },
                            chunk.index,
                            chunks.length,
                            chunk.start,
                            chunk.end,
                            outputKey
                        );

                        chunk.serverStatus = 'encoding';
                        updateAggregatedProgress();

                        let fetchFailed = false;
                        let fetchError: any = null;
                        fetchPromise.catch((err: any) => {
                            fetchFailed = true;
                            fetchError = err;
                        });

                        // Poll Firestore for completion
                        let url: string | null = null;
                        const startTime = Date.now();
                        const MAX_POLL_TIME_MS = 15 * 60 * 1000; // 15 mins max per chunk

                        while (!url && !this.aborted) {
                            if (fetchFailed) {
                                throw new Error(`Erreur réseau (Cloud Run inatteignable): ${fetchError?.message || 'Unknown error'}`);
                            }
                            if (Date.now() - startTime > MAX_POLL_TIME_MS) {
                                throw new Error(`Timeout: Le traitement du chunk a pris trop de temps.`);
                            }

                            await new Promise(r => setTimeout(r, 3000));

                            const status = await serverClient.getJobStatus(chunkJobId);

                            // Update server progress if available
                            if (status?.progress) {
                                chunk.serverProgress = status.progress;
                                updateAggregatedProgress();
                            }

                            if (status?.status === 'complete' && status.downloadUrl) {
                                url = status.downloadUrl;
                                chunk.serverStatus = 'complete';
                                chunk.serverProgress = 100;
                            } else if (status?.status === 'failed') {
                                chunk.serverStatus = 'failed';
                                throw new Error(`Server chunk ${chunk.index} failed: ${status.error}`);
                            }
                            // status === null or 'pending'/'downloading'/'processing'/'encoding' → keep waiting
                        }

                        if (this.aborted) return;

                        chunk.url = url!;
                        console.log(`[SmartHybrid] Chunk ${chunk.index} (Server) ready: ${url}`);

                        // Download to FS immediately to be ready for merge
                        chunk.localStatus = 'downloading';
                        updateAggregatedProgress();

                        await downloadChunkToFFmpegFS(chunk.index, url!, (idx, updates) => {
                            if (updates.localProgress) chunk.localProgress = updates.localProgress;
                            if (updates.size) chunk.size = updates.size;
                            updateAggregatedProgress(); // Real-time updates
                        });

                        chunk.localStatus = 'downloaded';
                        chunk.localProgress = 100;

                    } catch (e) {
                        console.error(`[SmartHybrid] Chunk ${chunk.index} failed`, e);
                        chunk.serverStatus = 'failed';
                        chunk.localStatus = 'failed';
                        throw e;
                    } finally {
                        completedChunks++;
                        updateAggregatedProgress();
                    }
                });

            // B. Run Local Jobs (Sequential)
            const localWork = async () => {
                const localChunks = chunks.filter(c => c.mode === 'local');
                for (const chunk of localChunks) {
                    if (this.aborted) break;

                    chunk.localStatus = 'downloading'; // Visual: "Working on it"
                    currentLocalChunkIndex = chunk.index; // Track which chunk is rendering for progress
                    updateAggregatedProgress();

                    const chunkClips = getClipsForChunk(clips, chunk.start, chunk.end);
                    console.log(`[SmartHybrid] Processing Local Chunk ${chunk.index}...`, chunkClips);

                    try {
                        // Export locally
                        const blob = await localExporter.export(
                            chunkClips,
                            tracks,
                            activeSubtitleLanguage,
                            subtitleStyle
                        );

                        chunk.blob = blob;
                        chunk.size = blob.size;

                        // Write to FS
                        // Convert blob to Uint8Array
                        const buf = await blob.arrayBuffer();
                        const data = new Uint8Array(buf);
                        await (await import('./ffmpeg-service')).FFmpegService.getInstance().then(f =>
                            f.writeFile(`chunk_${String(chunk.index).padStart(3, '0')}.mp4`, data)
                        );

                        chunk.localStatus = 'downloaded';
                        chunk.localProgress = 100;
                        console.log(`[SmartHybrid] Chunk ${chunk.index} (Local) ready`);

                    } catch (e) {
                        console.error(`[SmartHybrid] Local Chunk ${chunk.index} failed`, e);
                        chunk.localStatus = 'failed';
                        throw e;
                    } finally {
                        completedChunks++;
                        updateAggregatedProgress();
                    }
                }
            };

            // Wait for all
            await Promise.all([Promise.all(serverPromises), localWork()]);

            if (this.aborted) throw new Error('Export cancelled');

            // 3. Merge
            emitProgress('merging', 95, 'Assemblage final...');

            const localCount = chunks.filter(c => c.mode === 'local').length;
            const serverCount = chunks.filter(c => c.mode === 'server').length;
            const finalBlob = await mergeChunksWithFFmpeg(chunks.length, {
                reencode: localCount > 0 && serverCount > 0
            });

            emitProgress('complete', 100, 'Terminé !');
            return finalBlob;

        } catch (error: any) {
            console.error('[SmartHybrid] Export failed:', error);
            this.onProgress({ phase: 'failed', progress: 0, message: error.message });
            throw error;
        }
    }
}
